import { ensureGsap, prefersReducedMotion } from "@/shared/lib/gsap";
import { useEffect, useRef } from "react";

interface ParallaxOptions {
  /** Posun v px přes celé scroll okno (kladné = pomalejší než scroll → hloubka). Default 60. */
  amount?: number;
  /** Osa pohybu. Default "y". */
  axis?: "x" | "y";
}

/**
 * Decentní scroll-linked parallax. Element se posouvá pomaleji než stránka,
 * což dává hloubku. Scrub je plynulý (ne skokový), amplituda nízká.
 *
 * Respektuje prefers-reduced-motion (žádný pohyb) — stejně jako useGsapReveal.
 * Vrací ref k připnutí na cílový element (typicky dekorativní/pozadí vrstvu).
 */
export function useGsapParallax<T extends HTMLElement = HTMLDivElement>(
  options: ParallaxOptions = {},
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const gsap = ensureGsap();
    const el = ref.current;
    if (!gsap || !el) return;
    if (prefersReducedMotion()) return;

    const amount = options.amount ?? 60;
    const axis = options.axis ?? "y";
    const from = axis === "x" ? { x: -amount / 2 } : { y: -amount / 2 };
    const to = axis === "x" ? { x: amount / 2 } : { y: amount / 2 };

    const tween = gsap.fromTo(el, from, {
      ...to,
      ease: "none",
      scrollTrigger: {
        trigger: el,
        start: "top bottom",
        end: "bottom top",
        // Setrvačný doběh (ne skokový scrub) — prémiově hladký pocit (doporučení design review).
        scrub: 1.1,
      },
    });

    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
    };
  }, [options.amount, options.axis]);

  return ref;
}
